<template>
  <v-container fill-height>
    <!-- Full-page loader -->
    <div v-if="isLoading" class="d-flex align-center justify-center flex-column fill-height">
      <v-progress-circular indeterminate :color="colors.primary" size="64"></v-progress-circular>
    </div>

    <div v-else style="width:100%">
      <v-app-bar :elevation="2" :style="{ backgroundColor: colors.primary }">
        <v-app-bar-title :style="{ color: colors.secondary }">{{ $t('evaluation.title') }}</v-app-bar-title>
        <LanguageSwitcher />
        <v-menu>
          <template v-slot:activator="{ props }">
            <v-btn icon v-bind="props">
              <v-avatar size="36px">
                <v-img alt="Jury" :src="getJuryImageUrl(jury?.image)"></v-img>
              </v-avatar>
            </v-btn>
          </template>
          <v-list>
            <v-list-item @click="goToProfile">
              <v-list-item-title>{{ $t('menu.myProfile') }}</v-list-item-title>
            </v-list-item>
            <v-list-item @click="showResults">
              <v-list-item-title>{{ $t('menu.results') }}</v-list-item-title>
            </v-list-item>
            <v-list-item @click="logoutUser">
              <v-list-item-title>{{ $t('menu.logout') }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-app-bar>

      <!-- Tabs: Monthly | Pitch -->
      <v-tabs v-model="activeTab" :color="colors.primary" class="mt-2">
        <v-tab value="monthly" v-if="canAddMonthlyData">{{ $t('evaluation.tabMonthly') }}</v-tab>
        <v-tab value="pitch" v-if="canAddPitchData">{{ $t('evaluation.tabPitch') }}</v-tab>
      </v-tabs>

      <!-- ─── MONTHLY TAB ─── -->
      <v-window v-model="activeTab">
        <v-window-item value="monthly">
          <!-- Month + Candidate selectors -->
          <v-row class="mt-3 px-3" align="center">
            <v-col cols="12" sm="4">
              <v-select
                :items="candidatesList"
                item-title="name"
                item-value="id"
                v-model="currentCandidate"
                :label="$t('evaluation.selectCandidate')"
                outlined dense
                @update:modelValue="onCandidateOrMonthChanged"
              >
                <template v-slot:item="{ props, item }">
                  <v-list-item v-bind="props" :title="item.raw.name">
                    <template v-slot:prepend>
                      <v-icon v-if="isEvaluatedForMonth(item.raw.id)" color="green" size="small">mdi-check-circle</v-icon>
                    </template>
                  </v-list-item>
                </template>
                <template v-slot:selection="{ item }">
                  <span :style="isEvaluatedForMonth(item.raw.id) ? { color: colors.primary, fontWeight: 'bold' } : {}">
                    <v-icon v-if="isEvaluatedForMonth(item.raw.id)" color="green" size="small" class="mr-1">mdi-check-circle</v-icon>
                    {{ item.raw.name }}
                  </span>
                </template>
              </v-select>
            </v-col>
            <v-col cols="6" sm="3">
              <v-select
                :items="monthOptions"
                item-title="label"
                item-value="value"
                v-model="selectedMonth"
                :label="$t('evaluation.selectMonth')"
                outlined dense
                @update:modelValue="onMonthChanged"
              ></v-select>
            </v-col>
            <v-col cols="6" sm="2">
              <v-text-field
                v-model.number="selectedYear"
                :label="$t('evaluation.year')"
                outlined dense
                readonly
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- Edit mode label -->
          <v-alert v-if="currentCandidate && selectedMonth && hasExistingMonthlyScores" type="info" variant="tonal" class="mx-3 mb-2">
            {{ $t('evaluation.editingScoresFor', { month: currentMonthLabel, year: selectedYear }) }}
          </v-alert>

          <p v-if="!currentCandidate || !selectedMonth" class="text-center mt-4">
            {{ $t('evaluation.pleaseSelectCandidateAndMonth') }}
          </p>

          <!-- Criteria list -->
          <div v-else-if="!loadingRatings" class="px-3">
            <v-card class="mb-3" elevation="1">
              <v-card-text>
                <v-row v-for="(criterium, index) in monthlycriterias" :key="criterium.id" align="start" :class="['mb-2', index === 0 ? 'mt-8' : '']">
                  <v-col cols="12" md="6">
                    <p class="mb-1"><strong>{{ getCriteriaText(criterium) }}</strong></p>
                    <p class="text-caption text-medium-emphasis mb-0" v-html="getExplanationText(criterium)"></p>
                  </v-col>
                  <v-col cols="12" md="6">
                    <div class="d-flex align-center">
                      <v-slider
                        v-model="ratings[criterium.id]"
                        :max="criterium.max_score"
                        :min="0"
                        step="1"
                        thumb-label
                        :color="colors.primary"
                        show-ticks="always"
                        class="flex-grow-1"
                      ></v-slider>
                      <span class="ml-2 text-caption" style="min-width:60px">
                        {{ ratings[criterium.id] ?? 0 }} / {{ criterium.max_score }}
                      </span>
                    </div>
                    <v-textarea
                      v-model="notes[criterium.id]"
                      :label="$t('evaluation.commentOptional')"
                      variant="outlined"
                      density="compact"
                      rows="2"
                      auto-grow
                      hide-details
                      class="mt-1"
                    ></v-textarea>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" class="text-right">
                    <v-btn @click="submitMonthly" :color="colors.primary" :loading="submitDialog">
                      {{ $t('evaluation.submit') }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </div>
          <div v-else class="d-flex justify-center mt-6">
            <v-progress-circular indeterminate :color="colors.primary"></v-progress-circular>
          </div>
        </v-window-item>

        <!-- ─── PITCH TAB ─── -->
        <v-window-item value="pitch" v-if="canAddPitchData">
          <div class="px-3 mt-3">
            <!-- Candidate selector for pitch -->
            <v-select
              :items="candidatesList"
              item-title="name"
              item-value="id"
              v-model="pitchCandidate"
              :label="$t('evaluation.selectCandidate')"
              outlined dense
              class="mb-3"
              @update:modelValue="loadPitchRatings"
            >
              <template v-slot:item="{ props, item }">
                <v-list-item v-bind="props" :title="item.raw.name">
                  <template v-slot:prepend>
                    <v-icon v-if="isEvaluatedForPitch(item.raw.id)" color="green" size="small">mdi-check-circle</v-icon>
                  </template>
                </v-list-item>
              </template>
              <template v-slot:selection="{ item }">
                <span :style="isEvaluatedForPitch(item.raw.id) ? { color: colors.primary, fontWeight: 'bold' } : {}">
                  <v-icon v-if="isEvaluatedForPitch(item.raw.id)" color="green" size="small" class="mr-1">mdi-check-circle</v-icon>
                  {{ item.raw.name }}
                </span>
              </template>
            </v-select>

            <v-alert v-if="pitchCandidate && hasExistingPitchScores" type="info" variant="tonal" class="mb-3">
              {{ $t('evaluation.editingPitchFor', { year: selectedYear }) }}
            </v-alert>

            <p v-if="!pitchCandidate" class="text-center mt-4">{{ $t('evaluation.pleaseSelectCandidate') }}</p>

            <v-card v-else-if="!loadingPitchRatings" elevation="1">
              <v-card-text>
                <v-row v-for="(criterium, index) in pitchCriterias" :key="criterium.id" align="center" :class="['mb-2', index === 0 ? 'mt-8' : '']">
                  <v-col cols="12" md="6">
                    <p class="mb-1"><strong>{{ getCriteriaText(criterium) }}</strong></p>
                  </v-col>
                  <v-col cols="12" md="6">
                    <div class="d-flex align-center">
                      <v-slider
                        v-model="pitchRatings[criterium.id]"
                        :max="criterium.max_score"
                        :min="0"
                        step="1"
                        thumb-label
                        :color="colors.primary"
                        show-ticks="always"
                        class="flex-grow-1"
                      ></v-slider>
                      <span class="ml-2 text-caption" style="min-width:60px">
                        {{ pitchRatings[criterium.id] ?? 0 }} / {{ criterium.max_score }}
                      </span>
                    </div>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" class="text-right">
                    <v-btn @click="submitPitch" :color="colors.primary" :loading="submitPitchDialog">
                      {{ $t('evaluation.submit') }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
            <div v-else class="d-flex justify-center mt-6">
              <v-progress-circular indeterminate :color="colors.primary"></v-progress-circular>
            </div>
          </div>
        </v-window-item>
      </v-window>
    </div>

    <v-footer border app class="d-flex justify-center align-center p-4">
      {{ $t('common.developedBy') }} : <a href="https://thewebsquare.com" target="_blank" :style="{ color: colors.primary }">The Web Square</a>
    </v-footer>

    <!-- Success dialog -->
    <v-dialog v-model="submissionSuccessDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h5" justify="center" align="center">{{ $t('evaluation.evaluationSubmitted') }}</v-card-title>
        <v-card-text class="text-center">
          <div><v-icon icon="mdi-check" color="green" size="100"></v-icon></div>
          <p class="mt-2">{{ $t('evaluation.successMessage') }}</p>
        </v-card-text>
        <v-card-actions class="justify-center pb-4">
          <v-btn @click="submissionSuccessDialog = false" variant="outlined" :color="colors.primary">{{ $t('common.close') }}</v-btn>
          <v-btn @click="showResults" :color="colors.primary" variant="flat">{{ $t('evaluation.viewResults') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Error dialog -->
    <v-dialog v-model="showErrorDialog" max-width="320" persistent>
      <v-list class="py-2" color="red" elevation="12" rounded="lg">
        <v-list-item>
          <v-list-item-title justify="center" align="center">
            <div><v-icon icon="mdi-alert" color="red" size="100"></v-icon></div>
            <div>{{ $t('evaluation.submissionError') }}</div>
          </v-list-item-title>
          <v-list-item-action justify="center" align="center" style="justify-content:center;margin-top:10px;">
            <v-btn @click="showErrorDialog = false" :color="colors.primary">{{ $t('common.close') }}</v-btn>
          </v-list-item-action>
        </v-list-item>
      </v-list>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import colors from '../styles/colors';
import axios from 'axios';
import api from '../api';
import LanguageSwitcher from './LanguageSwitcher.vue';

export default {
  components: { LanguageSwitcher },
  data() {
    return {
      isLoading: true,
      loadingRatings: false,
      loadingPitchRatings: false,
      colors,
      activeTab: 'monthly',
      // Monthly
      currentCandidate: null,
      selectedMonth: new Date().getMonth() + 1,
      selectedYear: new Date().getFullYear(),
      candidatesList: [],
      monthlycriterias: [],
      pitchCriterias: [],
      ratings: {},
      notes: {},
      hasExistingMonthlyScores: false,
      evaluatedMonthlyMap: {},   // { '2026-5': [candidateId, ...] }
      // Pitch
      pitchCandidate: null,
      pitchRatings: {},
      hasExistingPitchScores: false,
      evaluatedPitchCandidates: [],
      // UI state
      submitDialog: false,
      submitPitchDialog: false,
      submissionSuccessDialog: false,
      showErrorDialog: false,
    };
  },
  computed: {
    ...mapState(['jury']),
    ...mapGetters(['canAddMonthlyData', 'canAddPitchData']),
    isEnglish() {
      return this.$i18n.locale === 'en';
    },
    monthOptions() {
      const fr = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
      const en = ['January','February','March','April','May','June','July','August','September','October','November','December'];
      const names = this.isEnglish ? en : fr;
      return names.map((label, i) => ({ label, value: i + 1 }));
    },
    currentMonthLabel() {
      const m = this.monthOptions.find(o => o.value === this.selectedMonth);
      return m ? m.label : '';
    },
  },
  methods: {
    ...mapActions(['logout', 'updateSelectedCandidate']),

    getCriteriaText(criterium) {
      return this.isEnglish && criterium.criteria_en ? criterium.criteria_en : criterium.criteria;
    },
    getExplanationText(criterium) {
      const explanation = this.isEnglish && criterium.explanation_en ? criterium.explanation_en : criterium.explanation;
      return explanation ? explanation.replace(/\n/g, '<br>') : '';
    },
    getJuryImageUrl(filename) { return api.getJuryImageUrl(filename); },
    getCandidateImageUrl(filename) { return api.getCandidateImageUrl(filename); },

    isEvaluatedForMonth(candidateId) {
      const key = `${this.selectedYear}-${this.selectedMonth}`;
      return (this.evaluatedMonthlyMap[key] || []).includes(candidateId);
    },

    isEvaluatedForPitch(candidateId) {
      return this.evaluatedPitchCandidates.includes(candidateId);
    },

    markMonthlyEvaluated(candidateId) {
      const key = `${this.selectedYear}-${this.selectedMonth}`;
      if (!this.evaluatedMonthlyMap[key]) {
        this.evaluatedMonthlyMap[key] = [];
      }
      if (!this.evaluatedMonthlyMap[key].includes(candidateId)) {
        this.evaluatedMonthlyMap[key].push(candidateId);
      }
    },

    async loadAllMonthlyStatus() {
      if (!this.selectedMonth || !this.candidatesList.length) return;
      await Promise.all(this.candidatesList.map(c =>
        axios.get(api.getCandidateRatingsByJury, {
          params: { jury_id: this.jury.id, candidate_id: c.id, month: this.selectedMonth, year: this.selectedYear },
        }).then(res => {
          const scores = res.data.scores || {};
          const hasScore = this.monthlycriterias.some(mc => scores[mc.id] !== undefined && scores[mc.id] !== null);
          if (hasScore) this.markMonthlyEvaluated(c.id);
        }).catch(() => {})
      ));
    },

    async loadAllPitchStatus() {
      if (!this.candidatesList.length) return;
      await Promise.all(this.candidatesList.map(c =>
        axios.get(api.getCandidateRatingsByJury, {
          params: { jury_id: this.jury.id, candidate_id: c.id, year: this.selectedYear },
        }).then(res => {
          const scores = res.data.scores || {};
          const hasScore = this.pitchCriterias.some(pc => scores[pc.id] !== undefined && scores[pc.id] !== null);
          if (hasScore && !this.evaluatedPitchCandidates.includes(c.id)) {
            this.evaluatedPitchCandidates.push(c.id);
          }
        }).catch(() => {})
      ));
    },

    async fetchInitialData() {
      try {
        const [candidatesRes, criteriasRes] = await Promise.all([
          axios.get(api.getCandidates, { params: { jury_id: this.jury.id } }),
          axios.get(api.getCriterias),
        ]);
        this.candidatesList = candidatesRes.data;
        const allCriterias = criteriasRes.data;
        this.monthlycriterias = allCriterias.filter(c => c.type === 'monthly').sort((a, b) => a.order - b.order);
        this.pitchCriterias = allCriterias.filter(c => c.type === 'pitch').sort((a, b) => a.order - b.order);
        this.monthlycriterias.forEach(c => { this.ratings[c.id] = 0; this.notes[c.id] = ''; });
        this.pitchCriterias.forEach(c => { this.pitchRatings[c.id] = 0; });
        // Pre-load checkmark status for current month and pitch
        await Promise.all([
          this.loadAllMonthlyStatus(),
          this.loadAllPitchStatus(),
        ]);
      } finally {
        this.isLoading = false;
      }
    },

    async onMonthChanged() {
      await this.loadAllMonthlyStatus();
      await this.onCandidateOrMonthChanged();
    },

    async onCandidateOrMonthChanged() {
      if (!this.currentCandidate || !this.selectedMonth) return;
      this.loadingRatings = true;
      this.hasExistingMonthlyScores = false;
      try {
        const res = await axios.get(api.getCandidateRatingsByJury, {
          params: {
            jury_id: this.jury.id,
            candidate_id: this.currentCandidate,
            month: this.selectedMonth,
            year: this.selectedYear,
          },
        });
        const scores = res.data.scores || {};
        const noteData = res.data.notes || {};
        this.monthlycriterias.forEach(c => {
          this.ratings[c.id] = scores[c.id] !== undefined ? Number(scores[c.id]) : 0;
          this.notes[c.id]   = noteData[c.id] || '';
        });
        const hasAnyScore = this.monthlycriterias.some(c => scores[c.id] !== undefined && scores[c.id] !== null);
        this.hasExistingMonthlyScores = hasAnyScore;
        if (hasAnyScore) this.markMonthlyEvaluated(this.currentCandidate);
      } catch (e) {
        console.error('Error loading ratings:', e);
      } finally {
        this.loadingRatings = false;
      }
    },

    async loadPitchRatings() {
      if (!this.pitchCandidate) return;
      this.loadingPitchRatings = true;
      this.hasExistingPitchScores = false;
      try {
        const res = await axios.get(api.getCandidateRatingsByJury, {
          params: {
            jury_id: this.jury.id,
            candidate_id: this.pitchCandidate,
            year: this.selectedYear,
          },
        });
        const scores = res.data.scores || {};
        this.pitchCriterias.forEach(c => {
          this.pitchRatings[c.id] = scores[c.id] !== undefined ? Number(scores[c.id]) : 0;
        });
        const hasAnyScore = this.pitchCriterias.some(c => scores[c.id] !== undefined && scores[c.id] !== null);
        this.hasExistingPitchScores = hasAnyScore;
        if (hasAnyScore && !this.evaluatedPitchCandidates.includes(this.pitchCandidate)) {
          this.evaluatedPitchCandidates.push(this.pitchCandidate);
        }
      } catch (e) {
        console.error('Error loading pitch ratings:', e);
      } finally {
        this.loadingPitchRatings = false;
      }
    },

    async submitMonthly() {
      this.submitDialog = true;
      try {
        const response = await axios.post(api.submitEvaluation, {
          jury_id: this.jury.id,
          candidate_id: this.currentCandidate,
          month: this.selectedMonth,
          year: this.selectedYear,
          ratings: this.ratings,
          notes: this.notes,
        });
        if (response.status === 200) {
          this.hasExistingMonthlyScores = true;
          this.markMonthlyEvaluated(this.currentCandidate);
          this.submissionSuccessDialog = true;
        }
      } catch (e) {
        this.showErrorDialog = true;
        console.error('Submit error:', e);
      } finally {
        this.submitDialog = false;
      }
    },

    async submitPitch() {
      this.submitPitchDialog = true;
      try {
        const response = await axios.post(api.submitEvaluation, {
          jury_id: this.jury.id,
          candidate_id: this.pitchCandidate,
          year: this.selectedYear,
          ratings: this.pitchRatings,
        });
        if (response.status === 200) {
          this.hasExistingPitchScores = true;
          if (!this.evaluatedPitchCandidates.includes(this.pitchCandidate)) {
            this.evaluatedPitchCandidates.push(this.pitchCandidate);
          }
          this.submissionSuccessDialog = true;
        }
      } catch (e) {
        this.showErrorDialog = true;
        console.error('Submit pitch error:', e);
      } finally {
        this.submitPitchDialog = false;
      }
    },

    goToProfile() { this.$router.push({ name: 'JuryProfile' }); },
    showResults() {
      this.submissionSuccessDialog = false;
      this.$router.push({ name: 'ResultsScreen' });
    },
    async logoutUser() {
      try { await axios.post(api.juryLogout); } catch (e) { /* ignore */ }
      this.logout();
      this.$nextTick(() => this.$router.push({ name: 'JuryLogin' }));
    },
  },
  mounted() {
    if (!this.canAddMonthlyData && this.canAddPitchData) {
      this.activeTab = 'pitch';
    }
    this.fetchInitialData();
  },
};
</script>
